import type { Asset } from '../types/ugla-types';
import type { LayerOptionValues, ProductOptionsMap } from '../types/vap-types';
import { getAssetsByLayerAndView, getControllableAssets } from './asset-helpers';
import { getProductLayer, getProductRef, getProductView, getTag } from './tag-helpers';

export const PRODUCT_OPTIONS_TAG = 'product_options';

/**
 * Parses a product_options tag value into a key/value map.
 * Format: "relax=ferme,color=bleu" → { relax: "ferme", color: "bleu" }
 */
export const parseProductOptions = (value: string): ProductOptionsMap => {
  const options: ProductOptionsMap = {};

  value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const separatorIndex = part.indexOf('=');
      if (separatorIndex === -1) {
        return;
      }

      const key = part.slice(0, separatorIndex).trim();
      const optionValue = part.slice(separatorIndex + 1).trim();
      if (key && optionValue) {
        options[key] = optionValue;
      }
    });

  return options;
};

/**
 * Serializes a product options map back to tag value format.
 */
export const serializeProductOptions = (options: ProductOptionsMap): string => {
  return Object.entries(options)
    .map(([key, value]) => `${key}=${value}`)
    .join(',');
};

/**
 * Reads product_options from an asset, if present.
 */
export const getProductOptionsFromAsset = (
  asset: { tags?: Array<{ key: string; value: string }> }
): ProductOptionsMap | undefined => {
  const rawValue = getTag(PRODUCT_OPTIONS_TAG, asset.tags);
  if (!rawValue) {
    return undefined;
  }

  const options = parseProductOptions(rawValue);
  return Object.keys(options).length > 0 ? options : undefined;
};

/**
 * Returns true when at least one controllable asset for the layer carries product_options.
 */
export const layerUsesProductOptions = (
  assets: Asset[],
  productLayer: string
): boolean => {
  return getControllableAssets(assets).some((asset) => {
    return getProductLayer(asset) === productLayer && getProductOptionsFromAsset(asset) !== undefined;
  });
};

/**
 * Collects option keys for a layer in first-seen order.
 */
export const getOptionKeysForLayer = (
  assets: Asset[],
  productView: string,
  productLayer: string
): string[] => {
  const keys: string[] = [];
  const seen = new Set<string>();

  getAssetsByLayerAndView(assets, productView, productLayer).forEach((asset) => {
    const options = getProductOptionsFromAsset(asset);
    if (!options) {
      return;
    }

    Object.keys(options).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    });
  });

  return keys;
};

/**
 * Collects all distinct values for one option key on a layer.
 */
export const getOptionValuesForLayer = (
  assets: Asset[],
  productView: string,
  productLayer: string,
  optionKey: string
): string[] => {
  const values = new Set<string>();

  getAssetsByLayerAndView(assets, productView, productLayer).forEach((asset) => {
    const options = getProductOptionsFromAsset(asset);
    const value = options?.[optionKey];
    if (value) {
      values.add(value);
    }
  });

  return Array.from(values).sort();
};

/**
 * Builds the full option matrix for a layer: { relax: [ouvert, ferme], color: [bleu, rouge] }.
 */
export const getAvailableOptionsForLayer = (
  assets: Asset[],
  productView: string,
  productLayer: string
): LayerOptionValues => {
  const availableOptions: LayerOptionValues = {};

  getOptionKeysForLayer(assets, productView, productLayer).forEach((optionKey) => {
    availableOptions[optionKey] = getOptionValuesForLayer(
      assets,
      productView,
      productLayer,
      optionKey
    );
  });

  return availableOptions;
};

/**
 * Checks whether an asset matches the selected option values exactly.
 */
export const assetMatchesProductOptions = (
  asset: Asset,
  selectedOptions: ProductOptionsMap
): boolean => {
  const assetOptions = getProductOptionsFromAsset(asset);
  if (!assetOptions) {
    return false;
  }

  return Object.entries(selectedOptions).every(
    ([key, value]) => assetOptions[key] === value
  );
};

/**
 * Finds the first asset matching all selected option values for a view/layer.
 */
export const findAssetByProductOptions = (
  assets: Asset[],
  productView: string,
  productLayer: string,
  selectedOptions: ProductOptionsMap
): Asset | undefined => {
  return getAssetsByLayerAndView(assets, productView, productLayer).find((asset) =>
    assetMatchesProductOptions(asset, selectedOptions)
  );
};

/**
 * Finds a representative asset for one option value, keeping other selected options fixed when possible.
 */
export const findAssetForOptionValue = (
  assets: Asset[],
  productView: string,
  productLayer: string,
  optionKey: string,
  optionValue: string,
  selectedOptions: ProductOptionsMap
): Asset | undefined => {
  const targetOptions = {
    ...selectedOptions,
    [optionKey]: optionValue,
  };

  const exactMatch = findAssetByProductOptions(
    assets,
    productView,
    productLayer,
    targetOptions
  );
  if (exactMatch) {
    return exactMatch;
  }

  return getAssetsByLayerAndView(assets, productView, productLayer).find((asset) => {
    const assetOptions = getProductOptionsFromAsset(asset);
    return assetOptions?.[optionKey] === optionValue;
  });
};

/**
 * Builds the initial option selection for a layer (first value per key).
 */
export const buildInitialOptionsForLayer = (
  assets: Asset[],
  productView: string,
  productLayer: string
): ProductOptionsMap => {
  const initialOptions: ProductOptionsMap = {};
  const availableOptions = getAvailableOptionsForLayer(assets, productView, productLayer);

  Object.entries(availableOptions).forEach(([optionKey, values]) => {
    if (values.length > 0) {
      initialOptions[optionKey] = values[0];
    }
  });

  return initialOptions;
};

/**
 * Resolves the product_ref for a layer from the current option selection.
 */
export const resolveProductRefFromOptions = (
  assets: Asset[],
  productView: string,
  productLayer: string,
  selectedOptions: ProductOptionsMap
): string | undefined => {
  const asset = findAssetByProductOptions(assets, productView, productLayer, selectedOptions);
  return asset ? getProductRef(asset) : undefined;
};
